// src/admin/AdminOffers.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';

function AdminOffers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const emptyForm = {
    title: '',
    message: '',
    code: '',
    cta_text: 'Profiter de l\'offre',
    cta_link: '/boutique',
    display_type: 'all',
    start_date: '',
    end_date: '',
    is_active: true,
    image: null,
  };
  const [form, setForm] = useState(emptyForm);

  const token = () => localStorage.getItem('auth_token');

  /* =============================
     CHARGER
  ============================= */
  const fetchOffers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('https://madadline.com/api/offers', {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setOffers(Array.isArray(data) ? data : []);
    } catch {
      setOffers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOffers(); }, []);

  /* =============================
     HANDLERS
  ============================= */
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'file' ? files[0] : value),
    }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditing(null);
    setShowForm(false);
    setMessage(null);
  };

  const startEdit = (offer) => {
    setForm({
      title: offer.title || '',
      message: offer.message || '',
      code: offer.code || '',
      cta_text: offer.cta_text || '',
      cta_link: offer.cta_link || '',
      display_type: offer.display_type || 'all',
      start_date: offer.start_date ? offer.start_date.slice(0, 16) : '',
      end_date: offer.end_date ? offer.end_date.slice(0, 16) : '',
      is_active: offer.is_active,
      image: null,
    });
    setEditing(offer.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'image') {
          if (v) fd.append('image', v);
        } else if (v !== null && v !== undefined) {
          fd.append(k, v === true ? 1 : v === false ? 0 : v);
        }
      });

      const url = editing
        ? `https://madadline.com/api/offers/${editing}`
        : 'https://madadline.com/api/offers';

      await axios.post(url, fd, {
        headers: {
          Authorization: `Bearer ${token()}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setMessage({ type: 'success', text: editing ? '✓ Offre modifiée' : '✓ Offre créée' });
      await fetchOffers();
      setTimeout(() => resetForm(), 1000);
    } catch (err) {
      const errs = err.response?.data?.errors;
      const first = errs ? Object.values(errs)[0]?.[0] : null;
      setMessage({ type: 'error', text: first || err.response?.data?.message || 'Erreur.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette offre ?')) return;
    try {
      await axios.delete(`https://madadline.com/api/offers/${id}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      await fetchOffers();
    } catch {
      alert('Erreur lors de la suppression.');
    }
  };

  const toggleActive = async (offer) => {
    const fd = new FormData();
    fd.append('is_active', offer.is_active ? 0 : 1);
    try {
      await axios.post(`https://madadline.com/api/offers/${offer.id}`, fd, {
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'multipart/form-data' },
      });
      await fetchOffers();
    } catch {
      alert('Erreur.');
    }
  };

  /* =============================
     RENDER
  ============================= */
  return (
    <div className="admin-offers">
      <header className="admin-page-head">
        <div>
          <h1>Offres & Pop-ups</h1>
          <p>{offers.length} offre{offers.length > 1 ? 's' : ''}</p>
        </div>
        <button
          className="admin-btn admin-btn--primary"
          onClick={() => { resetForm(); setShowForm(true); }}
        >
          + Nouvelle offre
        </button>
      </header>

      {message && (
        <div className={`admin-alert admin-alert--${message.type === 'success' ? 'success' : 'error'}`}>
          {message.text}
        </div>
      )}

      {/* FORMULAIRE */}
      {showForm && (
        <div className="admin-card">
          <div className="admin-card__head">
            <h2>{editing ? 'Modifier l\'offre' : 'Créer une offre'}</h2>
            <button className="admin-btn-sm admin-btn-sm--danger" onClick={resetForm}>✕ Fermer</button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="admin-form__grid">
              <div className="admin-form__group admin-form__group--full">
                <label>Titre *</label>
                <input
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="Ex: -15% ce week-end"
                  required
                />
              </div>

              <div className="admin-form__group admin-form__group--full">
                <label>Message *</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Profitez de -15% sur toute la collection..."
                  required
                />
              </div>

              <div className="admin-form__group">
                <label>Code promo</label>
                <input
                  name="code"
                  value={form.code}
                  onChange={handleChange}
                  placeholder="MADAD15"
                />
              </div>

              <div className="admin-form__group">
                <label>Type d'affichage *</label>
                <select name="display_type" value={form.display_type} onChange={handleChange}>
                  <option value="all">Partout</option>
                  <option value="newsletter">Après 5 secondes</option>
                  <option value="exit">Exit-intent (quitter)</option>
                  <option value="welcome">Première visite</option>
                </select>
              </div>

              <div className="admin-form__group">
                <label>Texte du bouton</label>
                <input
                  name="cta_text"
                  value={form.cta_text}
                  onChange={handleChange}
                  placeholder="Profiter de -15%"
                />
              </div>

              <div className="admin-form__group">
                <label>Lien du bouton</label>
                <input
                  name="cta_link"
                  value={form.cta_link}
                  onChange={handleChange}
                  placeholder="/boutique"
                />
              </div>

              <div className="admin-form__group">
                <label>Début</label>
                <input type="datetime-local" name="start_date" value={form.start_date} onChange={handleChange} />
              </div>

              <div className="admin-form__group">
                <label>Fin</label>
                <input type="datetime-local" name="end_date" value={form.end_date} onChange={handleChange} />
              </div>

              <div className="admin-form__group admin-form__group--full">
                <label>Image (optionnel)</label>
                <input type="file" name="image" accept="image/*" onChange={handleChange} />
              </div>

              <div className="admin-form__group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={form.is_active}
                    onChange={handleChange}
                    style={{ width: 'auto' }}
                  />
                  Active
                </label>
              </div>
            </div>

            <div className="admin-form__actions">
              <button type="submit" className="admin-btn admin-btn--primary" disabled={saving}>
                {saving ? 'Enregistrement…' : editing ? 'Mettre à jour' : 'Créer l\'offre'}
              </button>
              <button type="button" className="admin-btn" onClick={resetForm}>Annuler</button>
            </div>
          </form>
        </div>
      )}

      {/* LISTE */}
      {loading ? (
        <div className="admin-loading"><div className="spinner" /></div>
      ) : offers.length === 0 ? (
        <div className="admin-empty">
          <p>Aucune offre pour le moment.</p>
          <button className="admin-btn admin-btn--primary" onClick={() => setShowForm(true)}>
            Créer la première offre
          </button>
        </div>
      ) : (
        <div className="admin-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Titre</th>
                <th>Code</th>
                <th>Type</th>
                <th>Période</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {offers.map(o => (
                <tr key={o.id}>
                  <td><strong>{o.title}</strong></td>
                  <td><code>{o.code || '—'}</code></td>
                  <td>
                    <span className="admin-badge admin-badge--neutral">
                      {o.display_type === 'all' ? 'Partout' :
                       o.display_type === 'newsletter' ? 'Newsletter' :
                       o.display_type === 'exit' ? 'Exit-intent' : 'Accueil'}
                    </span>
                  </td>
                  <td>
                    {o.start_date || o.end_date ? (
                      <small>
                        {o.start_date ? new Date(o.start_date).toLocaleDateString('fr-FR') : '∞'}
                        {' → '}
                        {o.end_date ? new Date(o.end_date).toLocaleDateString('fr-FR') : '∞'}
                      </small>
                    ) : <small style={{ color: '#888' }}>Toujours</small>}
                  </td>
                  <td>
                    <button
                      onClick={() => toggleActive(o)}
                      className={`admin-badge admin-badge--${o.is_active ? 'success' : 'neutral'}`}
                      style={{ cursor: 'pointer', border: 'none' }}
                    >
                      {o.is_active ? '● Active' : '○ Inactive'}
                    </button>
                  </td>
                  <td>
                    <div className="admin-table__actions">
                      <button onClick={() => startEdit(o)} className="admin-btn-sm admin-btn-sm--edit">Modifier</button>
                      <button onClick={() => handleDelete(o.id)} className="admin-btn-sm admin-btn-sm--danger">Supprimer</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminOffers;